import CreationInvitationProgress from './CreationInvitationProgress';
import PendingInvitationList from './PendingInvitationList';
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock3, Loader2, MessageSquare, RotateCcw, UserCheck, UserMinus, UserPlus, Users } from 'lucide-react';
import { apiClient } from '../App';
import { isPastRun } from '../utils/datetime';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const uniquePlayers = (game) => {
  const players = [...(game?.team_a_players || []), ...(game?.team_b_players || [])];
  const seen = new Set();
  return players.filter((player) => {
    const id = String(player?.user_id || '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const displayName = (person, fallback = 'Player') => (
  person?.display_name || person?.username || fallback
);

const CountCard = ({ label, value, testId }) => (
  <div className="min-w-0 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid={testId}>
    <p className="text-lg font-semibold text-zinc-900">{value}</p>
    <p className="truncate text-xs text-zinc-500">{label}</p>
  </div>
);

const PersonRow = ({ name, status, action }) => (
  <li className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-zinc-100 bg-white px-3 py-2">
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-zinc-900">{name}</p>
      {status && <p className="text-xs text-zinc-500">{status}</p>}
    </div>
    {action}
  </li>
);

const HostPlayerManagementDialog = ({
  game,
  open,
  onOpenChange,
  onInvitePlayers,
  onMessagePlayers,
  onRemovePlayer,
  onPromoteWaitlist,
}) => {
  const [detail, setDetail] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [confirmationMap, setConfirmationMap] = useState({});
  const [checkInData, setCheckInData] = useState(null);
  const [checkInError, setCheckInError] = useState('');
  const [updatingCheckIn, setUpdatingCheckIn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open || !game?.id) return undefined;

    let active = true;
    setLoading(true);
    setLoadError(false);
    setDetail(game);
    setWaitlist([]);
    setWaitlistCount(Number(game.waitlist_count || 0));
    setConfirmationMap({});
    setCheckInData(null);
    setCheckInError('');

    Promise.allSettled([
      apiClient.get(`/games/${game.id}`),
      apiClient.get(`/games/${game.id}/waitlist`),
      apiClient.get(`/games/${game.id}/roster-status`),
      apiClient.get(`/games/${game.id}/check-ins`),
    ]).then(([gameResult, waitlistResult, confirmationResult, checkInResult]) => {
      if (!active) return;

      if (gameResult.status === 'fulfilled') {
        setDetail(gameResult.value.data);
      }
      if (waitlistResult.status === 'fulfilled') {
        const data = waitlistResult.value.data || {};
        setWaitlist(data.waitlist || []);
        setWaitlistCount(Number(data.waitlist_count ?? game.waitlist_count ?? 0));
      }
      if (confirmationResult.status === 'fulfilled') {
        setConfirmationMap(confirmationResult.value.data?.players || {});
      }
      if (checkInResult.status === 'fulfilled') {
        setCheckInData(checkInResult.value.data);
      }

      setLoadError(
        gameResult.status === 'rejected'
        && waitlistResult.status === 'rejected'
        && confirmationResult.status === 'rejected'
        && checkInResult.status === 'rejected'
      );
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [game, open]);

  const currentGame = detail || game;
  const joined = useMemo(() => uniquePlayers(currentGame), [currentGame]);
  const invited = currentGame?.invites_summary?.pending || [];
  const invitedCount = Number(currentGame?.invites_summary?.pending_count || invited.length || 0);
  const pendingConfirmationCount = joined.filter((player) => {
    const status = confirmationMap[player.user_id]?.status;
    return Boolean(status && status !== 'confirmed');
  }).length;
  const coHosts = currentGame?.co_hosts || [];
  const rosterLocked = isPastRun(currentGame?.date_time);
  const capacityKnown = currentGame?.max_players !== null
    && currentGame?.max_players !== undefined
    && Number.isFinite(Number(currentGame.max_players));
  const isFull = capacityKnown && joined.length >= Number(currentGame.max_players);
  const checkInByPlayer = useMemo(
    () => new Map(
      (Array.isArray(checkInData?.players) ? checkInData.players : [])
        .map((player) => [String(player.user_id), player])
    ),
    [checkInData]
  );

  const closeAndInvite = () => {
    onOpenChange(false);
    onInvitePlayers?.(currentGame);
  };

  const closeAndMessage = () => {
    onOpenChange(false);
    onMessagePlayers?.(currentGame);
  };

  const removePlayer = async (player) => {
    const removed = await onRemovePlayer?.(currentGame.id, player.user_id, displayName(player));
    if (removed) onOpenChange(false);
  };

  const promotePlayer = async (entry) => {
    const promoted = await onPromoteWaitlist?.(currentGame.id, entry.user_id, displayName(entry));
    if (promoted) onOpenChange(false);
  };

  const updatePlayerCheckIn = async (player) => {
    const playerId = String(player.user_id);
    const checkedIn = Boolean(checkInByPlayer.get(playerId)?.checked_in);
    setUpdatingCheckIn(playerId);
    setCheckInError('');
    try {
      const response = checkedIn
        ? await apiClient.delete(`/games/${currentGame.id}/check-ins/${playerId}`)
        : await apiClient.post(`/games/${currentGame.id}/check-ins/${playerId}`);
      setCheckInData(response.data);
    } catch (error) {
      setCheckInError(error.response?.data?.detail || 'Check-in could not be updated.');
    } finally {
      setUpdatingCheckIn(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="host-player-management-dialog">
        {open && game?.id && <CreationInvitationProgress gameId={game.id} />}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-500" />
            Players for {currentGame?.title || 'this session'}
          </DialogTitle>
          <DialogDescription>
            Factual participation and host roles for this session.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500" data-testid="host-players-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading players…
          </div>
        )}

        {!loading && (
          <div className="space-y-5">
            {loadError && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
                Some player details could not be loaded. Showing the facts already available.
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Player participation counts">
              <CountCard label="Joined" value={joined.length} testId="host-players-joined-count" />
              <CountCard label="Waitlisted" value={waitlistCount} testId="host-players-waitlist-count" />
              <CountCard label="Invited" value={invitedCount} testId="host-players-invited-count" />
              <CountCard label="Pending confirmation" value={pendingConfirmationCount} testId="host-players-pending-count" />
            </div>

            {checkInData?.window && (
              <section aria-labelledby="game-day-check-in-heading" className="rounded-xl border border-orange-200 bg-orange-50/60 p-3">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-orange-600" />
                  <h3 id="game-day-check-in-heading" className="text-sm font-semibold text-zinc-900">
                    Game-day check-in
                  </h3>
                </div>
                {checkInData.window?.available ? (
                  <div className="mt-3 grid grid-cols-3 gap-2" aria-label="Check-in progress">
                    <CountCard label="Checked in" value={checkInData.checked_in_count} testId="host-checked-in-count" />
                    <CountCard label="Joined" value={checkInData.joined_count} testId="host-check-in-joined-count" />
                    <CountCard label="Not yet checked in" value={checkInData.not_checked_in_count} testId="host-not-checked-in-count" />
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-zinc-600" data-testid="host-check-in-unavailable">
                    {checkInData.window?.reason === 'not_started'
                      ? 'Check-in opens at the scheduled start time.'
                      : 'Check-in is not available for this session.'}
                  </p>
                )}
                {checkInError && (
                  <p className="mt-2 text-sm text-red-700" role="alert">{checkInError}</p>
                )}
              </section>
            )}

            <section aria-labelledby="host-roles-heading">
              <h3 id="host-roles-heading" className="text-sm font-semibold text-zinc-900">Hosts</h3>
              <div className="mt-2 space-y-1 text-sm text-zinc-600">
                <p>
                  <span className="font-medium text-zinc-800">Host:</span>{' '}
                  {displayName(currentGame?.host, currentGame?.host_role === 'primary' ? 'You' : 'Primary host')}
                </p>
                <p>
                  <span className="font-medium text-zinc-800">Co-hosts:</span>{' '}
                  {coHosts.length > 0
                    ? coHosts.map((coHost) => displayName(coHost)).join(', ')
                    : 'None'}
                </p>
              </div>
            </section>

            <section aria-labelledby="joined-players-heading">
              <h3 id="joined-players-heading" className="text-sm font-semibold text-zinc-900">
                Joined ({joined.length})
              </h3>
              {joined.length === 0 ? (
                <p className="mt-2 text-sm text-zinc-500">No players have joined.</p>
              ) : (
                <ul className="mt-2 space-y-2" data-testid="host-joined-players">
                  {joined.map((player) => {
                    const confirmation = confirmationMap[player.user_id]?.status;
                    const playerCheckIn = checkInByPlayer.get(String(player.user_id));
                    const checkedIn = Boolean(playerCheckIn?.checked_in);
                    const status = confirmation === 'confirmed'
                      ? 'Confirmed'
                      : confirmation
                        ? 'Pending Confirmation'
                        : 'Joined';
                    return (
                      <PersonRow
                        key={player.user_id}
                        name={displayName(player)}
                        status={checkedIn ? `${status} · Checked in` : status}
                        action={checkInData?.window?.available ? (
                          <Button
                            type="button"
                            size="sm"
                            variant={checkedIn ? 'outline' : 'default'}
                            className={checkedIn
                              ? 'flex-shrink-0'
                              : 'flex-shrink-0 bg-orange-500 text-white hover:bg-orange-600'}
                            onClick={() => updatePlayerCheckIn(player)}
                            disabled={updatingCheckIn === String(player.user_id)}
                            aria-label={`${checkedIn ? 'Undo check-in for' : 'Check in'} ${displayName(player)}`}
                          >
                            {updatingCheckIn === String(player.user_id) ? (
                              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : checkedIn ? (
                              <RotateCcw className="mr-1 h-4 w-4" />
                            ) : (
                              <UserCheck className="mr-1 h-4 w-4" />
                            )}
                            {checkedIn ? 'Undo' : 'Check In'}
                          </Button>
                        ) : !rosterLocked && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="flex-shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => removePlayer(player)}
                            aria-label={`Remove ${displayName(player)}`}
                          >
                            <UserMinus className="mr-1 h-4 w-4" />
                            Remove
                          </Button>
                        )}
                      />
                    );
                  })}
                </ul>
              )}
            </section>

            <section aria-labelledby="waitlisted-players-heading">
              <h3 id="waitlisted-players-heading" className="text-sm font-semibold text-zinc-900">
                Waitlisted ({waitlistCount})
              </h3>
              {waitlist.length === 0 ? (
                <p className="mt-2 text-sm text-zinc-500">
                  {waitlistCount > 0 ? `${waitlistCount} on the waitlist.` : 'No players are waitlisted.'}
                </p>
              ) : (
                <ul className="mt-2 space-y-2" data-testid="host-waitlisted-players">
                  {waitlist.map((entry, index) => (
                    <PersonRow
                      key={entry.user_id}
                      name={displayName(entry)}
                      status={`Waitlist position ${entry.position || index + 1}`}
                      action={!rosterLocked && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isFull}
                          className="flex-shrink-0"
                          onClick={() => promotePlayer(entry)}
                          aria-label={`Promote ${displayName(entry)}`}
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" />
                          Promote
                        </Button>
                      )}
                    />
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="invited-players-heading">
              <h3 id="invited-players-heading" className="text-sm font-semibold text-zinc-900">
                Invited ({invitedCount})
              </h3>
              {open && <PendingInvitationList gameId={game.id} refreshKey={currentGame}
                testId="host-invited-players" renderPlayer={person => (
                  <PersonRow key={person.user_id} name={displayName(person)} status="Invited" />
                )} />}

            </section>

            {pendingConfirmationCount > 0 && (
              <p className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="host-pending-confirmation-note">
                <Clock3 className="h-4 w-4 flex-shrink-0" />
                {pendingConfirmationCount} joined player{pendingConfirmationCount === 1 ? '' : 's'} pending confirmation.
              </p>
            )}

            <div className="flex flex-col gap-2 border-t border-zinc-100 pt-4 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button type="button" variant="outline" onClick={closeAndMessage}>
                <MessageSquare className="mr-1.5 h-4 w-4" />
                Message Players
              </Button>
              <Button type="button" onClick={closeAndInvite} className="bg-orange-500 text-white hover:bg-orange-600">
                <UserPlus className="mr-1.5 h-4 w-4" />
                Invite Players
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HostPlayerManagementDialog;
